import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Modal, notification } from 'antd';

import {
  childLevelLabel,
  collectSecurityGroups,
  findKeyByPath,
  findNode,
  moveNode,
  newNode,
  removeNode,
  toApiNodes,
  toEditableNodes,
  updateNode,
  validateNodes,
} from '@totem/components/ticketing/categorizationManagement/categorizationUtilities';
import TicketCategorizationContext from '@totem/components/ticketing/categorizationManagement/TicketCategorizationContext';
import {
  CategorizationConfiguration,
  CategorizationCreateRequirements,
  CategorizationManagementResponse,
  EditableCategorizationNode,
  OrganizationReference,
} from '@totem/components/ticketing/categorizationManagement/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const CATEGORIZATION_ENDPOINT = `${TICKET_ENDPOINT}/categorization`;

type Props = {
  children?: ReactNode;
};

const TicketCategorizationContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [configuration, setConfiguration] =
    useState<CategorizationConfiguration | null>(null);
  const [baselineJson, setBaselineJson] = useState<string>('[]');
  const [nodes, setNodes] = useState<EditableCategorizationNode[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationReference[]>(
    [],
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const loadConfiguration = (
    config: CategorizationConfiguration,
    keepPath: string[] | null,
  ) => {
    const editable = toEditableNodes(config.data);
    setConfiguration(config);

    setNodes(editable);

    setBaselineJson(JSON.stringify(toApiNodes(editable)));

    setSelectedKey(
      keepPath !== null ? findKeyByPath(editable, keepPath) : null,
    );
  };

  useEffect(() => {
    if (!refreshData) {
      return;
    }
    setRefreshData(false);

    setLoading(true);

    fetch(CATEGORIZATION_ENDPOINT, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (!CheckResponseShowError(res)) {
          return null;
        }
        return res.json();
      })
      .then((result: CategorizationManagementResponse | null) => {
        if (result !== null) {
          loadConfiguration(result.configuration, null);

          setOrganizations(result.organizations ?? []);
        }
      })
      .finally(() => setLoading(false));
  }, [refreshData, getApiToken]);

  const isDirty = useMemo(
    () => JSON.stringify(toApiNodes(nodes)) !== baselineJson,
    [nodes, baselineJson],
  );

  // Warn before leaving the page with unsaved changes.
  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const validationErrors = useMemo(() => validateNodes(nodes), [nodes]);

  const selected = useMemo(
    () => (selectedKey !== null ? findNode(nodes, selectedKey) : null),
    [nodes, selectedKey],
  );

  const securityGroupOptions = useMemo(
    () => [...collectSecurityGroups(nodes)].sort(),
    [nodes],
  );

  const onAddNode = (parentKey: string | null) => {
    if (parentKey === null) {
      const node = newNode('New Ticket Type');
      setNodes((current) => [...current, node]);

      setSelectedKey(node.key);
      return;
    }
    const parent = findNode(nodes, parentKey);
    if (parent === null) {
      return;
    }
    const node = newNode(`New ${childLevelLabel(parent.level)}`);
    setNodes((current) =>
      updateNode(current, parentKey, (existing) => ({
        ...existing,
        children: [...existing.children, node],
      })),
    );

    setSelectedKey(node.key);
  };

  const onRenameNode = (key: string, name: string) => {
    setNodes((current) =>
      updateNode(current, key, (existing) => ({ ...existing, name })),
    );
  };

  const onUpdateRequirements = (
    key: string,
    requirements: Partial<CategorizationCreateRequirements>,
  ) => {
    setNodes((current) =>
      updateNode(current, key, (existing) => ({
        ...existing,
        createRequirements: { ...existing.createRequirements, ...requirements },
      })),
    );
  };

  const onRemoveNode = (key: string) => {
    setNodes((current) => removeNode(current, key));

    setSelectedKey(null);
  };

  const onMoveNode = (key: string, offset: number) => {
    setNodes((current) => moveNode(current, key, offset));
  };

  const onSave = () => {
    if (validationErrors.length > 0 || configuration === null) {
      return;
    }
    const keepPath =
      selected !== null
        ? [...selected.ancestors, selected.node].map((node) => node.name.trim())
        : null;

    setSaving(true);

    fetch(CATEGORIZATION_ENDPOINT, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify({
        data: toApiNodes(nodes),
        updatedAt: configuration.updatedAt ?? null,
      }),
    })
      .then((res) => {
        if (!CheckResponseShowError(res)) {
          return null;
        }
        return res.json();
      })
      .then((result: CategorizationConfiguration | null) => {
        if (result !== null) {
          loadConfiguration(result, keepPath);

          notification.success({
            message: 'Ticket categorization saved',
            duration: 3,
          });
        }
      })
      .finally(() => setSaving(false));
  };

  const onDiscard = () => {
    Modal.confirm({
      title: 'Discard unsaved changes?',
      content: 'All changes since the last save will be lost.',
      okText: 'Discard',
      okButtonProps: { danger: true },
      onOk: () => {
        if (configuration !== null) {
          loadConfiguration(configuration, null);
        }
      },
    });
  };

  const onRefresh = useCallback(() => {
    if (!isDirty) {
      setRefreshData(true);
      return;
    }
    Modal.confirm({
      title: 'Reload and discard unsaved changes?',
      content: 'All changes since the last save will be lost.',
      okText: 'Reload',
      okButtonProps: { danger: true },
      onOk: () => setRefreshData(true),
    });
  }, [isDirty]);

  return (
    <TicketCategorizationContext.Provider
      value={{
        loading,
        saving,
        configuration,
        nodes,
        organizations,
        securityGroupOptions,
        selectedKey,
        selected,
        isDirty,
        validationErrors,
        setSelectedKey,
        onAddNode,
        onRenameNode,
        onUpdateRequirements,
        onRemoveNode,
        onMoveNode,
        onSave,
        onDiscard,
        onRefresh,
      }}
    >
      {children}
    </TicketCategorizationContext.Provider>
  );
};

export default TicketCategorizationContainer;
